import { Button } from "@/components/ui/button";
import { useRed } from "@/context/RedContext";
import { DANA_ESKALACIJA, NAJVECI_RED } from "@/lib/red";

/**
 * What is still on this phone — the design system's "Unosi se spremaju na
 * telefon" and "Čeka slanje", ADR-0004 §2.8.
 *
 * Renders nothing when there is nothing waiting, which is how it stays out of
 * the way of everybody who never queues anything.
 *
 * The counter is not decoration. An entry stuck here past the seventh day is a
 * breach of NN 55/24 čl. 15, not an inconvenience, so the tone escalates with
 * the age rather than staying a neutral badge somebody learns to ignore.
 */
/**
 * "nije prihvaćen" in the number's own agreement.
 *
 * Croatian counts in three: one, two-to-four, five-and-up, and the last two
 * are not the same. Reading "2 unosa nije prihvaćeno" on a screen is the sort
 * of thing that makes people trust the rest of it less.
 */
function frazaOdbijenih(broj: number): string {
  const zadnja = broj % 10;
  const zadnje_dvije = broj % 100;
  if (zadnja === 1 && zadnje_dvije !== 11) {
    return "unos nije prihvaćen";
  }
  if (zadnja >= 2 && zadnja <= 4 && (zadnje_dvije < 12 || zadnje_dvije > 14)) {
    return "unosa nisu prihvaćena";
  }
  return "unosa nije prihvaćeno";
}

export function TrakaReda() {
  const { stanje, posaljiSad } = useRed();

  if (stanje.ceka === 0 && stanje.odbijeno === 0) {
    return null;
  }

  const hitno = stanje.zastarjelo || stanje.predugacak || stanje.odbijeno > 0;

  return (
    <section
      // `status` and not `alert`: this is standing information about the
      // device, not something that interrupts what somebody is typing.
      role="status"
      className={[
        "flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border p-4",
        hitno
          ? "border-[var(--color-status-warn-fg)]/30 bg-[var(--color-status-warn-bg)]"
          : "border-[var(--color-border)] bg-[var(--color-card)]",
      ].join(" ")}
    >
      <div className="min-w-0 flex-1">
        {/* One line, two different facts, separated the way the design system
            separates them elsewhere. They are counted apart because they need
            different things: one needs a signal, the other needs a person. */}
        <p className="font-medium">
          {stanje.ceka > 0 ? (
            <>
              <span className="broj">{stanje.ceka}</span>{" "}
              {stanje.ceka === 1 ? "unos čeka slanje" : "unosa čeka slanje"}
            </>
          ) : null}
          {stanje.ceka > 0 && stanje.odbijeno > 0 ? " · " : null}
          {stanje.odbijeno > 0 ? (
            <>
              <span className="broj">{stanje.odbijeno}</span>{" "}
              {frazaOdbijenih(stanje.odbijeno)}
            </>
          ) : null}
        </p>

        {stanje.odbijeno > 0 ? (
          <p className="text-[var(--color-status-warn-fg)]">
            Otvorite radnika na popisu za taj dan — piše zašto i može se
            ispraviti ili ukloniti.
          </p>
        ) : null}

        {stanje.zastarjelo && stanje.najstarijeDana !== null ? (
          <p className="text-[var(--color-status-warn-fg)]">
            Najstariji čeka <span className="broj">{stanje.najstarijeDana}</span>{" "}
            {stanje.najstarijeDana === 1 ? "dan" : "dana"}. Sati se moraju
            unijeti u roku od sedam dana
            {stanje.najstarijeDana >= DANA_ESKALACIJA
              ? " — kadrovska je o ovome obaviještena."
              : "."}
          </p>
        ) : null}

        {stanje.predugacak ? (
          <p className="text-[var(--color-status-warn-fg)]">
            Red je duži od {NAJVECI_RED} unosa. Slanje najvjerojatnije ne radi —
            javite se u ured.
          </p>
        ) : stanje.ceka > 0 ? (
          // Only said when something is actually waiting for a signal. A
          // refused entry never sends itself — it is waiting for a person —
          // and telling somebody it will go on its own is how it sits there
          // until payroll finds it missing (Z-11).
          <p className="text-[var(--color-muted-foreground)]">
            Unosi su spremljeni na telefon i šalju se sami kad ima mreže.
          </p>
        ) : null}
      </div>

      {stanje.ceka > 0 ? (
        <Button variant="outline" onClick={() => void posaljiSad()}>
          Pošalji sada
        </Button>
      ) : null}
    </section>
  );
}
